import React from "react";

import { Theme, useMediaQuery } from "@material-ui/core";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Loader from "components/Loader";
import NoContent from "components/NoContent";
import DashboardCard from "pages/DashboardPage/DashboardCard";
import Breakpoints from "common/breakpoints";
import DefaultExamRequirement from "./DefaultExamRequirement";
import DashboardCardTabs from "../DashboardCard/DashboardCardTabs";
import ExpiringExamRequirement from "./ExpiringExamRequirement";
import { useExamRequirements } from "./useExamRequirements";
import {
  ExamRequirementTab,
  examRequirementTabLabels,
  mobileExamRequirementTabLabels
} from "./options";

import useStyles from "./styles";

interface Props {
  className?: string;
}

const INITIAL_ITEMS_COUNT_LIMIT = 5;

const ExamRequirements = ({ className }: Props) => {
  const classes = useStyles();
  const matchesDownSX720 = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.SX_720)
  );

  const tabLabels = matchesDownSX720
    ? mobileExamRequirementTabLabels
    : examRequirementTabLabels;

  const {
    activeTab,
    horseExamRequirements,
    isLoading,
    isExpiringSoonTab,
    handleTabChange,
    updateExamRequirement
  } = useExamRequirements();

  const renderContent = () => {
    if (isLoading) return <Loader />;

    if (!horseExamRequirements.length) {
      return (
        <NoContent className={classes.noContent}>
          No exam requirements
        </NoContent>
      );
    }

    if (isExpiringSoonTab) {
      return horseExamRequirements.map(
        (item: racehorse360.HorseExamRequirement, index: number) => (
          <ExpiringExamRequirement
            key={index}
            horseName={item.horse.name}
            examType={item.type.description}
            reactivationDays={item.reactivationDays}
          />
        )
      );
    }

    return horseExamRequirements.map(item => (
      <DefaultExamRequirement
        key={item.id}
        examRequirement={item}
        onExamRequirementUpdate={updateExamRequirement}
      />
    ));
  };

  return (
    <DashboardCard
      className={className}
      contentClassName={classes.content}
      dataTestId="examRequirementsGrid"
      header={matchesDownSX720 && "Exam Forms"}
      initialItemsCount={matchesDownSX720 && INITIAL_ITEMS_COUNT_LIMIT}
      subHeader={
        <DashboardCardTabs
          activeTab={activeTab}
          enumValues={ExamRequirementTab}
          onTabChange={handleTabChange}
          tabLabels={tabLabels}
          totalNumber={horseExamRequirements?.length || 0}
        />
      }
    >
      {renderContent()}
    </DashboardCard>
  );
};

export default React.memo(ExamRequirements);
